// hooks/useModelData.js
import { useSelector, useDispatch } from 'react-redux';
import { useMemo, useEffect } from 'react';
import {
  selectImagePath,
  selectCurrentProduct,
  selectCurrProdName,
  selectRelativeProducts,
  setFormAdditionalRequirements,
  selectFormAdditionalRequirements,
  //selectFormPartNumber,
  // Add OCXO form selectors
  selectSelectedFrequency,
  selectSelectedMultiplier,
  selectSelectedSupplySignal,
  selectSelectedSupply,
  selectSelectedSignal,
  selectCustomStabilityFactor,
  selectCustomAgingFactor,
  selectCustomConsumptionFactor,
  // Add OCXO form actions
  setSelectedFrequency,
  setSelectedMultiplier,
  setSelectedSupplySignal,
  setSelectedSupply,
  setSelectedSignal,
  setCustomStabilityFactor,
  setCustomAgingFactor,
  setCustomConsumptionFactor,
  // Add new selectors for form data
  selectSelectedTemperatureRanges,
  selectSelectedAgingOptions,
  selectSelectedStabilityOptions,
  setSelectedTemperatureRanges,
  setSelectedAgingOptions,
  setSelectedStabilityOptions
} from '../store/productsSlice';

import { getPartNumberCode } from '../service/serviseApi';
export const useModelData = () => {
  const dispatch = useDispatch();
  
  // Get state from Redux
  const imagePath = useSelector(selectImagePath);
  const currentProduct = useSelector(selectCurrentProduct);
  
  // OCXO form state from Redux
  //const selectedFormPartNumber = useSelector(selectFormPartNumber);
  const selectedCurrProdName = useSelector(selectCurrProdName);
  const selectedFrequency = useSelector(selectSelectedFrequency);
  const selectedMultiplier = useSelector(selectSelectedMultiplier);
  const selectedSupplySignal = useSelector(selectSelectedSupplySignal);
  const selectedSupply = useSelector(selectSelectedSupply);
  const selectedSignal = useSelector(selectSelectedSignal);
  const selectedFormAdditionalRequirements = useSelector(selectFormAdditionalRequirements);
  const customStabilityFactor = useSelector(selectCustomStabilityFactor);
  const customAgingFactor = useSelector(selectCustomAgingFactor);
  const customConsumptionFactor = useSelector(selectCustomConsumptionFactor);
  
  // Add new form data selectors
  const selectedTemperatureRanges = useSelector(selectSelectedTemperatureRanges);
  const selectedAgingOptions = useSelector(selectSelectedAgingOptions);
  const selectedStabilityOptions = useSelector(selectSelectedStabilityOptions);

  // Memoize derived data
  const currentProductViewerData = useMemo(() => {
    if (!currentProduct?.imagesViewer) return [];
    const scaleImage = currentProduct?.scaleImage || 1;

    return currentProduct.imagesViewer.map(image => ({
      large: `${imagePath}${image.large}`,
      thumb: `${imagePath}${image.thumb}`,
      scaleImage: scaleImage,
      alt: image.alt
    }));
  }, [currentProduct, imagePath]);

  const currentProductToDo = useMemo(() => {
    return currentProduct?.toDo || '';
  }, [currentProduct]);

  const relativeProducts = useSelector(selectRelativeProducts);

  // Get part number configuration data from current product
  const partNumberConfig = useMemo(() => {
    return currentProduct?.partNumberServiceData || null;
  }, [currentProduct]);

  // Memoize all derived form data
  const formData = useMemo(() => ({
    tempRange: selectedTemperatureRanges,
    frequency: selectedFrequency,
    supplySignal: selectedSupplySignal,
    supply: selectedSupply,
    signal: selectedSignal,
    aging: selectedAgingOptions,
    stability: selectedStabilityOptions,
    additionalRequirements: selectedFormAdditionalRequirements,
    //isupply, isignal, ifrequency, iproductName, itempRange, istability, iaging
    partNumber: getPartNumberCode(
      selectedSupply,
      selectedSignal,
      selectedFrequency,
      selectedCurrProdName,
      selectedTemperatureRanges,
      selectedStabilityOptions,
      selectedAgingOptions
    ),
    }), [
      //selectedFormPartNumber,
      selectedTemperatureRanges,
      selectedFrequency,
      selectedSupplySignal,
      selectedSupply,
      selectedSignal,
      selectedAgingOptions,
      selectedStabilityOptions
    ]);

  // Actions
  const actions = useMemo(() => ({
    // OCXO Analysis actions
    setSelectedFrequency: (frequency) => dispatch(setSelectedFrequency(frequency)),
    setSelectedMultiplier: (multiplier) => dispatch(setSelectedMultiplier(multiplier)),
    setSelectedSupplySignal: (signal) => dispatch(setSelectedSupplySignal(signal)),
    setSelectedSupply: (supply) => {dispatch(setSelectedSupply(supply))},
    setSelectedSignal: (signal) => dispatch(setSelectedSignal(signal)),
    setCustomStabilityFactor: (factor) => dispatch(setCustomStabilityFactor(factor)),
    setCustomAgingFactor: (factor) => dispatch(setCustomAgingFactor(factor)),
    setCustomConsumptionFactor: (factor) => dispatch(setCustomConsumptionFactor(factor)),
    // New form actions
    setSelectedTemperatureRanges: (tempRange) => dispatch(setSelectedTemperatureRanges(tempRange)),
    setSelectedAgingOptions: (aging) => dispatch(setSelectedAgingOptions(aging)),
    setSelectedStabilityOptions: (stability) => dispatch(setSelectedStabilityOptions(stability)),
    setFormAdditionalRequirements: (text) => dispatch(setFormAdditionalRequirements(text)),
  }), [dispatch]);
  
  return {
    // Form state
    formData,
    //selectedFormPartNumber,
    selectedTemperatureRanges,
    selectedFrequency,
    selectedSupplySignal,
    selectedSupply,
    selectedSignal,
    selectedAgingOptions,
    selectedStabilityOptions,
    selectedFormAdditionalRequirements,
    // Configuration data
    partNumberConfig,
    
    // Other product data
    imagePath,
    relativeProducts,
    currentProductViewerData,
    currentProductToDo,
    currentProduct,
    
    // Actions
    actions
  };
};